/**
 * Business-wide frontend migration helpers for login-time routing only.
 */
import type {
  BillingContext,
  BusinessFrontendMode,
} from "@smartwiz/shared-types";

import type {
  Role,
  UserAuthData,
} from "@pages/LoginManagement/types/login.types";

import { AppRoutes } from "@/constants.ts";
import {
  getIntakeClientHomeUrl,
  getIntakeGettingStartedUrl,
  getIntakeProHomeUrl,
  getIntakeServiceBureauHomeUrl,
} from "@/utils/intakeUrl";

/**
 * True when the user's business has fully migrated to the Smart Intake frontend.
 *
 * Replaces the retired SMART_INTAKE feature flag, which only recorded that an
 * account had once been pointed at intake — it was granted on the way in and
 * never revoked on the way out.
 */
export function isIntakeFrontend(
  user: Pick<UserAuthData, "frontendMode"> | null | undefined
): boolean {
  return user?.frontendMode === "intake";
}

/** True when the owner may persist an intake frontend choice (active contract). */
export function isOwnerIntakeMigrationEligible(user: UserAuthData): boolean {
  return user.intakeMigrationEligible === true;
}

/** True when intake migration should route through managed onboarding instead of CRM upgrade. */
export function isManagedBusinessForIntakeMigration(
  billingContext: BillingContext | null | undefined
): boolean {
  return billingContext?.mode === "managed_by_service_bureau";
}

/** Resolve where to send an owner who must upgrade before migrating to intake. */
export function getIntakeMigrationPrerequisiteDestination(
  billingContext: BillingContext | null | undefined
): string {
  if (isManagedBusinessForIntakeMigration(billingContext)) {
    return AppRoutes.gettingStarted;
  }
  return AppRoutes.contractUpgrade;
}

/** Resolve the intake destination for a user at login time. */
export function getIntakeLoginDestination(user: UserAuthData): string {
  if (user.isServiceBureau) {
    return getIntakeServiceBureauHomeUrl();
  }
  if (user.role === "client") {
    return getIntakeClientHomeUrl();
  }
  return getIntakeProHomeUrl();
}

/**
 * Where a pro goes the moment they choose to migrate.
 *
 * The onboarding wizard, not pro home — and the difference is a firm's whole
 * back catalogue of questions. `/getting-started` carries the `legacyOrganizer`
 * step, which is shown only to a firm that wrote questions in the old organizer
 * and is the one place that offers to bring them over and then reports what
 * happened to each one. Dropping a migrating pro straight onto pro home skips
 * it silently: nothing else in the app tells them their questions are waiting,
 * so they meet a product that appears to have lost their work.
 *
 * Owners only. A team member or a client has no firm-level setup to do, so they
 * go where they were always going.
 */
export function getIntakeMigrationDestination(user: UserAuthData): string {
  if (user.isServiceBureau || user.role === "client") {
    return getIntakeLoginDestination(user);
  }
  if (user.role !== "owner") {
    return getIntakeLoginDestination(user);
  }
  return getIntakeGettingStartedUrl();
}

/** True when the intake frontend migration rollout is active for this session. */
export function isIntakeFrontendMigrationActive(user: UserAuthData): boolean {
  return Boolean(
    user.intakeFrontendMigrationEnabled ?? user.intakeFrontendRedirectsEnabled
  );
}

/** Returns true when an admin or service bureau impersonation cookie is active. */
export function isImpersonationSession(): boolean {
  try {
    const cookies = document.cookie.split(";");
    const serviceBureauCookie = cookies.find((cookie) =>
      cookie.trim().startsWith("service_bureau_impersonation_info=")
    );
    const adminCookie = cookies.find((cookie) =>
      cookie.trim().startsWith("admin_impersonation_info=")
    );

    const parseFlag = (
      rawCookie: string | undefined,
      flag: "is_service_bureau_impersonation" | "is_impersonation"
    ): boolean => {
      if (!rawCookie) return false;
      const value = decodeURIComponent(rawCookie.split("=").slice(1).join("="));
      const parsed = JSON.parse(value) as Record<string, unknown>;
      return parsed[flag] === true;
    };

    return (
      parseFlag(serviceBureauCookie, "is_service_bureau_impersonation") ||
      parseFlag(adminCookie, "is_impersonation")
    );
  } catch {
    return false;
  }
}

/** Returns true when legacy register links should redirect to intake signup. */
export function shouldRedirectLegacyRegisterToIntake(
  mode: BusinessFrontendMode | undefined,
  migrationEnabled: boolean
): boolean {
  return migrationEnabled && mode === "intake";
}

/** Returns true when login should send the user to Smart Intake. */
export function shouldRedirectToIntakeAtLogin(user: UserAuthData): boolean {
  if (!isIntakeFrontendMigrationActive(user)) {
    return false;
  }
  const mode = user.frontendMode ?? "unset";
  if (mode === "intake") {
    return true;
  }
  if (mode === "intake_pending_onboarding") {
    return user.role !== "owner";
  }
  return false;
}

/** Returns true when an owner must choose legacy vs intake once. */
export function shouldShowOwnerMigrationModal(
  user: UserAuthData,
  pathname: string = ""
): boolean {
  if (isMigrationPrerequisitePath(pathname)) {
    return false;
  }
  // An offer the firm cannot accept is not an offer. Without an active contract
  // the page's own primary action refuses, so an un-dismissable screen sat over
  // an app the pro could not use — including over the page that tells them to
  // renew. A lapsed firm now sees the ordinary app (or its renewal page), and
  // meets this the moment their contract is good again.
  if (!isOwnerIntakeMigrationEligible(user)) {
    return false;
  }
  return (
    !isImpersonationSession() &&
    Boolean(user.intakeFrontendMigrationEnabled) &&
    (user.frontendMode ?? "unset") === "unset" &&
    user.role === "owner"
  );
}

/**
 * The pages the modal must not cover: the ones a pro has to complete to become
 * eligible in the first place.
 *
 * The modal is deliberately un-dismissable — no close button, no escape, no
 * click-outside — so on `/contract-upgrade` it covered the very page the pro had
 * to complete, and its only action was to navigate to the page they were already
 * on. The button appeared dead and the contract could not be signed.
 *
 * `/subscription-expired` is the same trap and was missed: `ProtectedRoute`
 * sends an owner whose subscription has lapsed there to renew, and the modal
 * blanketed it — a firm whose contract had run out could neither renew nor read
 * the page telling them to. Seen live on staging 2026-08-25. Anywhere the app
 * routes an inactive owner belongs in this list.
 */
export function isMigrationPrerequisitePath(pathname: string): boolean {
  return (
    pathname.startsWith(AppRoutes.contractUpgrade) ||
    pathname.startsWith(AppRoutes.gettingStarted) ||
    pathname.startsWith(AppRoutes.subscriptionExpired)
  );
}

/** True when the banner may call the owner choice API to upgrade to intake. */
export function canOwnerUpgradeToIntakeFromBanner(user: UserAuthData): boolean {
  return (
    user.role === "owner" &&
    isIntakeFrontendMigrationActive(user) &&
    user.frontendMode === "legacy"
  );
}

/** Returns true when the persistent legacy deprecation banner should render. */
export function shouldShowLegacyDeprecationBanner(user: UserAuthData): boolean {
  if (isImpersonationSession()) {
    return false;
  }
  if (user.role !== "owner" || !isIntakeFrontendMigrationActive(user)) {
    return false;
  }
  const mode = user.frontendMode ?? "unset";
  if (mode === "intake") {
    return false;
  }
  if (mode === "legacy") {
    return true;
  }
  if (mode === "intake_pending_onboarding") {
    return true;
  }
  return false;
}

/** Resolve the intake destination for a role at login time. */
export function getIntakeLoginDestinationForRole(role: Role): string {
  if (role === "client") {
    return getIntakeClientHomeUrl();
  }
  return getIntakeProHomeUrl();
}

/** Normalize backend frontend mode values for UI comparisons. */
export function normalizeFrontendMode(
  mode: BusinessFrontendMode | undefined
): BusinessFrontendMode {
  return mode ?? "unset";
}

/**
 * Fill in migration fields omitted transiently during ``refreshAuth``.
 *
 * Used only when the session-check response omits optional migration keys;
 * explicit values on ``nextUser`` always win (``??`` semantics). Do not use
 * this to override a deliberate backend mode change — the API must send the
 * authoritative ``frontendMode`` on every full auth payload.
 */
export function mergeFrontendMigrationAuthFields(
  nextUser: UserAuthData,
  previousUser: UserAuthData | null
): UserAuthData {
  if (!previousUser || previousUser.id !== nextUser.id) {
    return nextUser;
  }

  return {
    ...nextUser,
    frontendMode: nextUser.frontendMode ?? previousUser.frontendMode,
    legacyFrontendSunsetAt:
      nextUser.legacyFrontendSunsetAt ?? previousUser.legacyFrontendSunsetAt,
    intakeFrontendMigrationEnabled:
      nextUser.intakeFrontendMigrationEnabled ??
      previousUser.intakeFrontendMigrationEnabled,
    intakeMigrationEligible:
      nextUser.intakeMigrationEligible ?? previousUser.intakeMigrationEligible,
    intakeFrontendRedirectsEnabled:
      nextUser.intakeFrontendRedirectsEnabled ??
      previousUser.intakeFrontendRedirectsEnabled,
  };
}

/** Apply a confirmed frontend mode to the in-memory auth user. */
export function withFrontendMode(
  user: UserAuthData,
  frontendMode: BusinessFrontendMode
): UserAuthData {
  return {
    ...user,
    frontendMode,
  };
}
